import React from 'react';

import {SlotsPlaceholder} from '@wix/widget-plugins-ooi';
import classNames from 'classnames';
import {Layout, ProductPageSlotIds} from '../../../constants';
import {SPECS} from '../../../specs';
import {LayoutComponentProps} from '../../../types/app-types';
import {ConditionalRender} from '../../ConditionalRender/ConditionalRender';
import {Navigation} from '../../Navigation/Navigation';
import {DetailsPlaceholder} from '../../Placeholders/DetailsPlaceholder';
import {ProductDescription} from '../../ProductDescription/ProductDescription';
import {ProductDiscountName} from '../../ProductDiscountName/ProductDiscountName';
import {ProductGalleryLayout} from '../../ProductGallery/ProductGalleryLayout/ProductGalleryLayout';
import {createLayoutConfigWithDefaults} from '../../ProductGallery/ProductGalleryLayout/ProductGalleryLayoutUtils';
import {ProductName} from '../../ProductName/ProductName';
import {ProductOptions} from '../../ProductOptions/ProductOptions';
import {ProductPageButtonsContainer} from '../../ProductPageButtonsContainer/ProductPageButtonsContainer';
import {ProductPageTopSlot} from '../../ProductPageTopSlot/ProductPageTopSlot';
import {ProductPaymentBreakdown} from '../../ProductPaymentBreakdown/ProductPaymentBreakdown';
import {ProductPrice} from '../../ProductPrice/ProductPrice';
import {ProductRibbonsOnProductInfo} from '../../ProductRibbons/ProductRibbons';
import {ProductSku} from '../../ProductSku/ProductSku';
import {ProductSocial} from '../../ProductSocial/ProductSocial';
import {RatingSummarySlot} from '../../RatingSummarySlot/RatingSummarySlot';
import {Cell} from '../Cell/Cell';
import s from './SpotlightLayout.scss';

export const SpotlightLayoutHeader: React.FunctionComponent<LayoutComponentProps> = ({settings, globals}) => {
  const GALLERY_WIDTH = 580;
  const GALLERY_HEIGHT = 420;

  const {product, hasDiscountName, hasPaymentOptionsBreakdown, useExperiments} = globals;
  const addDiscountsToVariantsEnabled = useExperiments.enabled(SPECS.AddDiscountsToVariantsItemsQueries);
  const discountName = addDiscountsToVariantsEnabled
    ? product.itemDiscount?.automaticDiscountRuleNames?.slice(0, 2).join(' \u00B7 ')
    : product.itemDiscount?.discountRuleName;
  const headerClass = classNames(s.header, {[s.fullWidth]: settings.isFullWidth});
  const {renderProductPageSlots} = globals.experiments ?? {};
  const isPluginInstalledInDetails1Slot = !!globals.slots?.[ProductPageSlotIds.ProductPageDetails1];
  const isPluginInstalledInDetails2Slot = !!globals.slots?.[ProductPageSlotIds.ProductPageDetails2];
  const isPluginInstalledInDetails3Slot = !!globals.slots?.[ProductPageSlotIds.ProductPageDetails3];
  const isPluginInstalledInDetails4Slot = !!globals.slots?.[ProductPageSlotIds.ProductPageDetails4];
  const productPageDescriptionToggle = useExperiments.enabled(SPECS.ProductPageDescriptionToggle);

  return (
    <div className={s.headerContainer}>
      <div className={headerClass}>
        <ProductPageTopSlot />
        <ConditionalRender by="productPage_navigation">
          <Navigation className={s.navigation} />
        </ConditionalRender>
        <article>
          <section className={s.col}>
            <Cell className={s.gallery}>
              <ProductGalleryLayout
                product={product}
                layoutConfig={createLayoutConfigWithDefaults({
                  withDynamicHeight: true,
                  dimensions: {
                    mainMedia: {
                      widthConf: {num: GALLERY_WIDTH, unit: 'px'},
                      heightConf: {num: GALLERY_HEIGHT, unit: 'px'},
                    },
                    thumbnails: {
                      widthConf: {num: GALLERY_WIDTH, unit: 'px'},
                      heightConf: {num: 50, unit: 'px'},
                    },
                  },
                })}
                layout={Layout.Spotlight}
                {...settings}
              />
            </Cell>
          </section>
          <section className={s.col}>
            <ProductRibbonsOnProductInfo />
            <Cell>
              <ProductName name={product.name} />
            </Cell>
            {renderProductPageSlots && (
              <Cell className={classNames({[s.productPageSlot]: isPluginInstalledInDetails1Slot})}>
                <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails1} />
              </Cell>
            )}
            <RatingSummarySlot />
            <ConditionalRender by="productPage_productSku">
              <Cell className={s.sku}>
                <ProductSku />
              </Cell>
            </ConditionalRender>

            {renderProductPageSlots && (
              <Cell className={classNames({[s.productPageSlot]: isPluginInstalledInDetails2Slot})}>
                <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails2} />
              </Cell>
            )}

            <ConditionalRender by="productPage_productPrice">
              <Cell className={s.price}>
                <ProductPrice />
              </Cell>
            </ConditionalRender>

            {renderProductPageSlots && (
              <Cell className={classNames({[s.productPageSlot]: isPluginInstalledInDetails3Slot})}>
                <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails3} />
              </Cell>
            )}

            {hasDiscountName && (
              <ConditionalRender by="productPage_showDiscountName">
                <Cell>
                  <ProductDiscountName name={discountName} />
                </Cell>
              </ConditionalRender>
            )}

            {hasPaymentOptionsBreakdown && (
              <ConditionalRender by="productPage_paymentOptionsBreakdown">
                <Cell>
                  <ProductPaymentBreakdown />
                </Cell>
              </ConditionalRender>
            )}

            <Cell>
              <DetailsPlaceholder />
            </Cell>

            {productPageDescriptionToggle ? (
              <ConditionalRender by="productPage_productDescription">
                <Cell className={s.description}>
                  <ProductDescription description={product.description} />
                </Cell>
              </ConditionalRender>
            ) : (
              <Cell className={s.description}>
                <ProductDescription description={product.description} />
              </Cell>
            )}

            {renderProductPageSlots && (
              <Cell className={classNames({[s.productPageSlotMediumSpacing]: isPluginInstalledInDetails4Slot})}>
                <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails4} />
              </Cell>
            )}

            <Cell className={s.optionsAndButtonsContainer}>
              <ProductOptions className={s.options} />
              <ProductPageButtonsContainer />
            </Cell>

            <ConditionalRender by="productPage_socialNetworks">
              <Cell className={s.social}>
                <ProductSocial />
              </Cell>
            </ConditionalRender>
          </section>
        </article>
      </div>
    </div>
  );
};
